// Personal settings — notification channel + per-type toggles.
// Teams toggle stays clickable but shows a hint until the Teams-App is
// installed (Story 5b finishes that path).
//
// Redesign (Paket: Settings-Navigation): 15 <h2>-Abschnitte lagen zuvor
// ungebrochen hintereinander — man scrollte und suchte. Folgt jetzt dem
// gleichen Muster wie routes/Admin.tsx: die Abschnitte werden nach Zweck in
// 5 Gruppen sortiert und ueber ViewTabs im Kopfzeilen-`tabs`-Steckplatz
// erreichbar gemacht. Die Gruppen-Inhalte selbst liegen aus Platzgruenden
// (500-Zeilen-Grenze) in routes/settings/*Group.tsx — reine Verschiebung,
// diese Datei bleibt die einzige mit Zustand/Logik. Nur WO ein Abschnitt
// gerendert wird aendert sich — jede Unterkomponente (SpacesSection,
// ApiTokensSection, BotsSection, TemplatesSection) behaelt ihre Props
// unveraendert.

import { useEffect, useState } from "preact/hooks";
import { Palette, SlidersHorizontal, Bell, KeyRound, LayoutGrid } from "lucide-preact";
import { Layout } from "../components/Layout";
import { PageShell } from "../components/ui/PageShell";
import { ViewTabs } from "../components/ui/ViewTabs";
import { usePageHeader } from "../lib/pageHeader";
import { requireLabel } from "../lib/navigation";
import { fetchPreferences, updatePreferences } from "../store/notifications";
import { toastSuccess, toastError } from "../lib/toast";
import { api, getMailConnection } from "../lib/api";
import { DEFAULT_USER_PREFERENCES, type UserPreferences, type MailConnectionDto } from "@juuplan/shared";
import { loadThemeChoice, saveThemeChoice, type ThemeChoice } from "../lib/theme";
import { loadDensity, saveDensity, type Density } from "../lib/density";
import { loadLayout, saveLayout, type LayoutStyle } from "../lib/layout";
import {
  loadDefaultFilter,
  loadDefaultView,
  saveDefaultFilter,
  saveDefaultView,
  type DefaultProjectView,
  type DefaultTaskFilter,
} from "../lib/userDefaults";
import { useDepartments } from "../store/departments";
import { useLists } from "../store/personalTasks";
import { personalLists, writableLists } from "../lib/defaultList";
import { AppearanceGroup } from "./settings/AppearanceGroup";
import { DefaultsGroup } from "./settings/DefaultsGroup";
import { NotificationsGroup } from "./settings/NotificationsGroup";
import { AccountGroup } from "./settings/AccountGroup";
import { WorkspaceGroup } from "./settings/WorkspaceGroup";

const DEFAULTS: UserPreferences = DEFAULT_USER_PREFERENCES;

// Gruppierung der 15 Abschnitte nach Zweck (nicht nach der bisherigen
// Reihenfolge): Erscheinungsbild = reines Aussehen, Standardwerte = was ohne
// weiteres Zutun voreingestellt ist, Benachrichtigungen = Kanal + Anlass,
// Konto & Zugang = Postfach/Tokens/Bots, Arbeitsbereich = Spaces/Templates.
const GROUPS = [
  { id: "erscheinungsbild", label: "Erscheinungsbild", icon: Palette },
  { id: "standardwerte", label: "Standardwerte", icon: SlidersHorizontal },
  { id: "benachrichtigungen", label: "Benachrichtigungen", icon: Bell },
  { id: "konto", label: "Konto & Zugang", icon: KeyRound },
  { id: "arbeitsbereich", label: "Arbeitsbereich", icon: LayoutGrid },
] as const;

type SettingsGroupId = (typeof GROUPS)[number]["id"];

// Der gewaehlte Reiter muss einen Neuladen ueberleben. Anders als bei
// routes/Admin.tsx (Sektion im Routen-Pfad `/admin/:section?`) gibt es fuer
// `/settings` bewusst KEINEN neuen Pfad-Parameter — Deep-Links aus der
// iOS-App und aus E-Mails zeigen auf den nackten Pfad. Zwei Kandidaten
// blieben: URL-Hash oder localStorage. Entscheidung: localStorage, aus zwei
// Gruenden — (1) es passt zum bereits etablierten Muster GENAU dieser Datei
// (Theme/Dichte/Layout-Stil liegen schon in localStorage, siehe lib/theme.ts,
// lib/density.ts, lib/layout.ts); (2) der URL-Hash ist im Projekt schon fuer
// Scroll-/Deep-Link-Anker reserviert (store/deepLinks.ts haengt `#anker` an
// Task-Deep-Links) — ein zweiter Hash-Verbraucher auf derselben Seite waere
// eine Kollisionsquelle ohne echten Zusatznutzen (kein Anwendungsfall braucht
// einen TEILBAREN Link auf einen bestimmten Settings-Reiter).
const SETTINGS_TAB_KEY = "juuplan:settings-tab";

function loadSettingsTab(): SettingsGroupId {
  if (typeof localStorage === "undefined") return GROUPS[0].id;
  const raw = localStorage.getItem(SETTINGS_TAB_KEY);
  return GROUPS.some((g) => g.id === raw) ? (raw as SettingsGroupId) : GROUPS[0].id;
}

function saveSettingsTab(id: SettingsGroupId): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(SETTINGS_TAB_KEY, id);
}

export function Settings(): preact.JSX.Element {
  const [prefs, setPrefs] = useState<UserPreferences | null>(null);
  const [saving, setSaving] = useState(false);
  const [theme, setTheme] = useState<ThemeChoice>(loadThemeChoice());
  const [density, setDensity] = useState<Density>(loadDensity());
  const [layout, setLayoutState] = useState<LayoutStyle>(loadLayout());
  const [defaultView, setDefaultViewState] = useState<DefaultProjectView>(loadDefaultView());
  const [defaultFilter, setDefaultFilterState] = useState<DefaultTaskFilter>(loadDefaultFilter());
  const [activeGroup, setActiveGroup] = useState<SettingsGroupId>(loadSettingsTab());
  const departments = useDepartments();
  const { lists } = useLists();
  const [mailConn, setMailConn] = useState<MailConnectionDto | null>(null);

  useEffect(() => {
    // fetchPreferences() is a GET — api() only auto-toasts mutation methods —
    // so a failure here was silent apart from quietly falling back to
    // DEFAULTS, which looks identical to "nothing customized yet".
    void fetchPreferences()
      .then(setPrefs)
      .catch(() => {
        toastError("Einstellungen konnten nicht geladen werden — zeige Standardwerte.");
        setPrefs(DEFAULTS);
      });
  }, []);

  const loadMailConn = (): void => {
    // getMailConnection() passes silentError: true — "kein Postfach verbunden"
    // is the expected default state, not an error worth a toast.
    void getMailConnection().then(setMailConn).catch(() => setMailConn(null));
  };

  useEffect(() => {
    loadMailConn();
    // Surface the connect-mailbox redirect result, then clean the URL.
    const params = new URLSearchParams(window.location.search);
    const m = params.get("mailbox");
    if (m === "connected") toastSuccess("Postfach verbunden");
    else if (m === "error") toastError("Postfach konnte nicht verbunden werden");
    if (m) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const disconnectMailbox = (): void => {
    void (async () => {
      try {
        await api<void>("/api/auth/disconnect-mailbox", { method: "POST" });
        toastSuccess("Postfach getrennt");
        loadMailConn();
      } catch {
        toastError("Trennen fehlgeschlagen");
      }
    })();
  };

  const pickTheme = (next: ThemeChoice): void => {
    setTheme(next);
    saveThemeChoice(next);
    toastSuccess("Theme aktualisiert");
  };

  const pickDensity = (next: Density): void => {
    setDensity(next);
    saveDensity(next);
    toastSuccess("Zeilenhöhe aktualisiert");
  };

  const pickLayout = (next: LayoutStyle): void => {
    setLayoutState(next);
    saveLayout(next);
    toastSuccess("Layout-Stil aktualisiert");
  };

  const pickDefaultView = (next: DefaultProjectView): void => {
    setDefaultViewState(next);
    saveDefaultView(next);
    toastSuccess("Standard-Sicht aktualisiert");
  };

  const pickDefaultFilter = (next: DefaultTaskFilter): void => {
    setDefaultFilterState(next);
    saveDefaultFilter(next);
    toastSuccess("Standard-Filter aktualisiert");
  };

  const pickGroup = (id: string): void => {
    const group = GROUPS.find((g) => g.id === id);
    if (!group) return;
    setActiveGroup(group.id);
    saveSettingsTab(group.id);
  };

  // Lists the user may create tasks in — personal plus shared-as-editor. The
  // first personal list is what the "Automatisch" option resolves to.
  const selectableLists = writableLists(lists);
  const firstPersonalList = personalLists(lists)[0];

  const update = (patch: Partial<UserPreferences>): void => {
    if (!prefs) return;
    const previous = prefs;
    const next = { ...prefs, ...patch };
    setPrefs(next);
    setSaving(true);
    void (async () => {
      try {
        const result = await updatePreferences(patch);
        setPrefs(result);
        // Globalen Cache aktualisieren, damit Sidebar/Projects/MyTasks die
        // neuen visible_departments sofort sehen.
        const mod = await import("../store/userPrefs");
        mod.setUserPrefs(result);
        toastSuccess("Einstellungen gespeichert");
      } catch {
        // api() has already toasted the failure — roll the optimistic value back
        // so the control doesn't claim a setting the server rejected.
        setPrefs(previous);
      } finally {
        setSaving(false);
      }
    })();
  };

  usePageHeader({
    title: requireLabel("/settings"),
    subtitle: "Steuere wie und worüber dich juuplan benachrichtigt.",
    tabs: (
      <ViewTabs
        items={GROUPS.map((g) => ({ id: g.id, label: g.label, icon: g.icon }))}
        active={activeGroup}
        onChange={pickGroup}
        label="Einstellungs-Bereiche"
      />
    ),
  });

  const loadingHint = (
    <p class="text-sm text-[color:var(--color-on-surface-variant)] italic">Lädt…</p>
  );

  return (
    <Layout>
      {/* narrow: reine Formularflaeche (max-w-2xl = 672px, nahe an narrow = 768px). */}
      <PageShell width="narrow">
        {activeGroup === "erscheinungsbild" && (
          <AppearanceGroup
            theme={theme}
            onTheme={pickTheme}
            layout={layout}
            onLayout={pickLayout}
            density={density}
            onDensity={pickDensity}
          />
        )}
        {activeGroup === "standardwerte" && (
          <DefaultsGroup
            defaultView={defaultView}
            onDefaultView={pickDefaultView}
            defaultFilter={defaultFilter}
            onDefaultFilter={pickDefaultFilter}
            prefs={prefs}
            saving={saving}
            selectableLists={selectableLists}
            firstPersonalList={firstPersonalList}
            departments={departments}
            onUpdate={update}
            loadingHint={loadingHint}
          />
        )}
        {activeGroup === "benachrichtigungen" && (
          <NotificationsGroup
            prefs={prefs}
            saving={saving}
            onUpdate={update}
            loadingHint={loadingHint}
          />
        )}
        {activeGroup === "konto" && (
          <AccountGroup mailConn={mailConn} onDisconnectMailbox={disconnectMailbox} />
        )}
        {activeGroup === "arbeitsbereich" && <WorkspaceGroup />}
      </PageShell>
    </Layout>
  );
}
